import { useRef, useState } from 'react'
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, EllipsisVertical, EyeOff, Plus } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useClickOutside } from '@/lib/useClickOutside'

export type SortDir = 'asc' | 'desc'

/**
 * Per-column "⋮" dropdown shared by the deals and contacts tables:
 * sort asc/desc (when {@code sortable}), reorder left/right (when move handlers are given),
 * add a hidden column, remove (locked columns can't be removed).
 * Generic over the column-id type so each table keeps its own id union.
 */
export default function ColumnHeaderMenu<Id extends string>({
  canRemove,
  onSort,
  onRemove,
  hiddenColumns,
  onAdd,
  sortable = true,
  onMoveLeft,
  onMoveRight,
  canMoveLeft = false,
  canMoveRight = false,
  align = 'right',
}: {
  canRemove: boolean
  onSort: (dir: SortDir) => void
  onRemove: () => void
  hiddenColumns: { id: Id; label: string }[]
  onAdd: (id: Id) => void
  /** When false, the sort options are hidden (e.g. custom columns with no server sort key). */
  sortable?: boolean
  onMoveLeft?: () => void
  onMoveRight?: () => void
  canMoveLeft?: boolean
  canMoveRight?: boolean
  /** Which edge the dropdown anchors to. Left-most columns should use 'left' so the menu opens
   *  rightward instead of off the table's left edge (where it gets clipped). */
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)
  const itemCls =
    'flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs font-normal normal-case tracking-normal text-gray-600 hover:bg-gray-50 hover:text-brand-dark'

  function pick(action: () => void) {
    action()
    setOpen(false)
  }

  return (
    <div
      ref={ref}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Column options"
        aria-expanded={open}
        className={cn(
          'p-0.5 rounded text-gray-400 hover:text-brand-dark focus:opacity-100',
          open ? 'opacity-100 text-brand-dark' : 'opacity-0 group-hover:opacity-100',
        )}
      >
        <EllipsisVertical size={13} />
      </button>
      {open && (
        <div
          // Anchor away from the table edge: right-most columns open leftward (right-0); the left-most
          // column opens rightward (left-0) so the menu isn't clipped off the table's left edge.
          // Cap the height and scroll for long add-column lists.
          className={cn(
            'absolute top-full z-20 mt-1 w-44 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1',
            align === 'left' ? 'left-0' : 'right-0',
          )}
        >
          {sortable && (
            <>
              <button type="button" onClick={() => pick(() => onSort('asc'))} className={itemCls}>
                <ArrowUp size={13} /> Sort ascending
              </button>
              <button type="button" onClick={() => pick(() => onSort('desc'))} className={itemCls}>
                <ArrowDown size={13} /> Sort descending
              </button>
            </>
          )}
          {onMoveLeft && (
            <button
              type="button"
              disabled={!canMoveLeft}
              onClick={() => pick(onMoveLeft)}
              className={cn(itemCls, !canMoveLeft && 'opacity-40 hover:bg-transparent cursor-default')}
            >
              <ArrowLeft size={13} /> Move left
            </button>
          )}
          {onMoveRight && (
            <button
              type="button"
              disabled={!canMoveRight}
              onClick={() => pick(onMoveRight)}
              className={cn(itemCls, !canMoveRight && 'opacity-40 hover:bg-transparent cursor-default')}
            >
              <ArrowRight size={13} /> Move right
            </button>
          )}
          {canRemove && (
            <button type="button" onClick={() => pick(onRemove)} className={itemCls}>
              <EyeOff size={13} /> Remove column
            </button>
          )}
          {hiddenColumns.length > 0 && (
            <>
              <div className="my-1 border-t border-gray-100" />
              <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
                <Plus size={11} /> Add column
              </div>
              {hiddenColumns.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => pick(() => onAdd(h.id))}
                  className={itemCls}
                >
                  {h.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}
